import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { ApiError, api } from '../lib/api';
import { NavDrawer } from './NavDrawer';
import { ErrorNote, Frame } from './ui';
import { UserMenu } from './UserMenu';

/**
 * `scoped` marks the tabs that show an organization's own data. A platform
 * administrator sees them only once they have entered one: outside, the
 * server refuses every one of those pages, and a tab that cannot be opened
 * is worse than no tab.
 */
const NAV = [
  {
    to: '/classes',
    label: 'Attendance',
    roles: ['teacher', 'org_admin', 'super_admin'],
    scoped: true,
  },
  {
    to: '/reports',
    label: 'Reports',
    roles: ['teacher', 'org_admin', 'super_admin'],
    scoped: true,
  },
  { to: '/manage/students', label: 'Students', roles: ['org_admin', 'super_admin'], scoped: true },
  { to: '/manage/classes', label: 'Classes', roles: ['org_admin', 'super_admin'], scoped: true },
  { to: '/manage/users', label: 'Staff', roles: ['org_admin', 'super_admin'], scoped: true },
  { to: '/manage/calendar', label: 'Holidays', roles: ['org_admin', 'super_admin'], scoped: true },
  { to: '/platform', label: 'Organizations', roles: ['super_admin'], scoped: false },
  { to: '/platform/people', label: 'People', roles: ['super_admin'], scoped: false },
] as const;

export function Shell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const session = useQuery({
    queryKey: ['me'],
    queryFn: api.me,
    // Not signed in is an answer, not a failure, so it is never retried. A
    // server that is briefly unreachable is worth one more attempt.
    retry: (attempts, error) =>
      !(error instanceof ApiError && error.status === 401) && attempts < 2,
  });

  const signedOut = session.error instanceof ApiError && session.error.status === 401;

  useEffect(() => {
    if (signedOut) void navigate({ to: '/login' });
  }, [signedOut, navigate]);

  const signOut = useMutation({
    mutationFn: api.logout,
    onSuccess: () => {
      queryClient.clear();
      void navigate({ to: '/login' });
    },
  });

  if (session.isLoading) {
    return (
      <div className="grid min-h-dvh place-items-center">
        <p className="eyebrow">Loading</p>
      </div>
    );
  }

  // Redirecting to sign in; rendering the shell for an instant would flash a
  // masthead belonging to nobody.
  if (signedOut) return null;

  /**
   * Anything else that stopped the session resolving - the API down, a 500, a
   * database it cannot reach. Only 401 means "go and sign in", so without this
   * branch every other failure rendered nothing at all and the whole app looked
   * like a blank page with no way forward.
   */
  if (session.error || !session.data) {
    return (
      <Frame
        title="Cannot reach the server"
        subtitle="The app is running; its API is not answering."
      >
        <div className="grid gap-4">
          <ErrorNote
            message={
              session.error instanceof ApiError
                ? `${session.error.message} (${session.error.status})`
                : 'No response from the server.'
            }
          />
          <button type="button" className="btn btn-primary" onClick={() => void session.refetch()}>
            Try again
          </button>
          <Link to="/login" className="btn">
            Go to sign in
          </Link>
        </div>
      </Frame>
    );
  }

  const user = session.data;
  const items = NAV.filter(
    (item) =>
      (item.roles as readonly string[]).includes(user.role) &&
      (!item.scoped || user.organization !== null),
  );

  /**
   * The most specific matching tab wins. A plain `startsWith` would light up
   * both Organizations and People on /platform/people, because one path is a
   * prefix of the other.
   */
  const activeTo = items
    .filter((item) => pathname === item.to || pathname.startsWith(`${item.to}/`))
    .reduce<string | null>(
      (best, item) => (best && best.length >= item.to.length ? best : item.to),
      null,
    );

  const heading = user.organization?.name ?? 'Platform';

  /*
   * Two mastheads in one row. Below `sm` it is a phone's: the Menu button,
   * the name of where you are, the account menu, on one line that does not
   * wrap (a long organization name truncates rather than pushing the account
   * button onto a second line). The brand is left out there: on a 360px
   * screen it and the account name between them leave "Grace Church" no room
   * to be read, and the app's name is on the home screen icon and at the top
   * of the drawer. From `sm` up the tabs sit in the row itself and the Menu
   * button is gone; that side is unchanged.
   */
  return (
    <div className="min-h-dvh">
      <header className="border-b border-rule bg-raised">
        <div className="mx-auto flex max-w-[1400px] flex-nowrap items-center gap-x-4 gap-y-3 px-4 py-2.5 sm:flex-wrap sm:gap-x-8 sm:px-6 sm:py-3">
          <NavDrawer items={items} activeTo={activeTo} heading={heading} pathname={pathname} />

          <div className="flex min-w-0 items-baseline gap-3">
            <span className="hidden shrink-0 font-mono text-[13px] font-semibold tracking-[0.18em] uppercase sm:inline">
              Quire
            </span>
            {user.organization ? (
              <span className="truncate text-sm text-ink-soft">{user.organization.name}</span>
            ) : (
              <span className="eyebrow">Platform</span>
            )}
          </div>

          <nav className="hidden flex-wrap items-center gap-1 sm:flex">
            {items.map((item) => {
              const active = item.to === activeTo;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  // A tablet shows this row and taps it. An inline box takes
                  // no min-height, hence inline-flex under a coarse pointer.
                  className="rounded px-2.5 py-1.5 text-sm transition-colors pointer-coarse:inline-flex pointer-coarse:min-h-11 pointer-coarse:items-center"
                  style={{
                    background: active ? 'var(--color-paper-sunk)' : 'transparent',
                    color: active ? 'var(--color-ink)' : 'var(--color-ink-soft)',
                    fontWeight: active ? 600 : 400,
                  }}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="ml-auto shrink-0">
            <UserMenu
              user={user}
              onSignOut={() => signOut.mutate()}
              onSwitchOrganization={() => void navigate({ to: '/enter' })}
              signingOut={signOut.isPending}
            />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-4 py-5 sm:px-6 sm:py-8">
        <Outlet />
      </main>
    </div>
  );
}
